import { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { jbColors } from 'theme/jbColors';
import { theme } from 'theme';
import MemberCodeIcon from 'assets/icons/Membercode.svg?react';
import { setSelectedLanguage, setTermsAndCondition } from 'reducers/generateCodeDetailsReducer';
import { openModal } from 'reducers/modalReducer';
import { selectEmailLanguage, selectIsTermsAndConditionAccepted } from 'selectors/generateCodeDetailsSelector';
import { LanguageEnum } from 'pco-product-service-internal-consumer';
import { RootState } from 'store';
import { MessageBox } from 'components/common/MessageBox';

const styles = {
  labelRoot: {
    marginLeft: 0,
    marginTop: 0,
    marginRight: 0,
    width: '100%',
  },
  labelPlacementTop: {
    alignItems: 'flex-start',
  },
  // TODO - remove this and put to default
  textInput: {
    boxSizing: 'border-box',
    marginTop: theme.spacing(0.5),
    width: '100%',
    '& .MuiFilledInput-inputMarginDense': {
      paddingLeft: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: '100%',
    },
    '& .MuiFilledInput-multiline.MuiFilledInput-marginDense': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiFilledInput-inputMultiline': {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
    '& .MuiFilledInput-input': {
      boxSizing: 'border-box',
    },
  },
  exclamationIcon: {
    width: 16,
    height: 16,
  },
  alert: {
    '& .MuiAlert-message': {
      paddingTop: 5,
    },
    color: jbColors.alert[100],
    backgroundColor: jbColors.alert[80],
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: jbColors.alert[100],
    height: '30px',
  },
  disabledLabel: {
    '& .MuiFormControlLabel-label.Mui-disabled': {
      color: 'black',
    },
  },
  loading: {
    opacity: 0.3,
    position: 'absolute',
    width: '600px',
    height: '459px',
    zIndex: 1,
    backgroundColor: jbColors.support.greenSmoke[80],
  },
};

export const CreateAccessCodeForm = () => {
  const dispatch = useDispatch();
  const selectedLanguageValue = useSelector(selectEmailLanguage);
  const partnerForAccessCode = useSelector((state: RootState) => {
    return state.partner.partnerForAccessCode;
  });

  const isTaCAccepted = useSelector(selectIsTermsAndConditionAccepted);

  const handleClickTSCS = () => {
    dispatch(
      openModal({
        modalType: 'TermsAndConditionsDialog',
        modalProps: {},
      }),
    );
  };

  const handleEmailLanguageChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setSelectedLanguage((event.target as HTMLInputElement).value as LanguageEnum));
  };

  const handleTaCChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    dispatch(setTermsAndCondition(checked));
  };

  return (
    <Card id={'create-activation-code-form'} style={{ maxWidth: '90%' }} square>
      <Grid container style={{ paddingLeft: theme.spacing(2) }}>
        <MemberCodeIcon style={{ marginTop: theme.spacing(2) }} />
        <CardHeader
          title={'CREATE ACTIVATION CODE'}
          titleTypographyProps={{ variant: 'subtitle1' }}
          style={{
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(1),
          }}
        />
      </Grid>
      <CardContent style={{ paddingTop: theme.spacing(1) }}>
        <Grid container spacing={2} style={{ paddingTop: theme.spacing(0) }}>
          <Grid item xs={6}>
            <FormLabel id={'memberzone-first-name-label'}>First name</FormLabel>
            <TextField
              id={'memberzone-first-name-text-field'}
              data-testid={'memberzone-first-name-text-field'}
              disabled
              value={partnerForAccessCode?.firstName}
              variant="filled"
              fullWidth
              margin={'dense'}
              sx={styles.textInput}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormLabel id={'memberzone-last-name-label'}>Last name</FormLabel>
            <TextField
              id={'memberzone-last-name-field'}
              data-testid={'memberzone-last-name-text-field'}
              value={partnerForAccessCode?.lastName}
              disabled
              variant="filled"
              fullWidth
              margin={'dense'}
              sx={styles.textInput}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
          <Grid item xs={6}>
            <FormLabel id={'memberzone-main-domicile-label'}>Main domicile (country)</FormLabel>

            <TextField
              id={'memberzone-main-domicile-text-field'}
              data-testid={'memberzone-main-domicile-text-field'}
              value={
                partnerForAccessCode?.partnerAddresses?.postalAddresses?.find((address) =>
                  address.usageType?.includes('MAIN_DOMICILE'),
                )?.country || 'N/A'
              }
              variant="filled"
              fullWidth
              margin={'dense'}
              sx={styles.textInput}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid item xs={6}>
            {/*<Grid item xs={ 6 }>*/}
            <FormLabel id={'memberzone-email-language-label'}>Email language</FormLabel>
            <RadioGroup
              row
              aria-label="Email language"
              name="EN"
              value={selectedLanguageValue}
              onChange={handleEmailLanguageChange}
            >
              {Object.values(LanguageEnum).map((value, index) => (
                <FormControlLabel
                  value={value}
                  control={<Radio id={`memberzone-radio-${value}`} color={'primary'} />}
                  label={value}
                  key={`email-language-${value}-${index}`}
                />
              ))}
            </RadioGroup>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: theme.spacing(2) }}>
          <Checkbox
            id={'terms-and-condition-checkbox'}
            checked={isTaCAccepted}
            sx={{ pl: 1 }}
            onChange={handleTaCChange}
            color="primary"
            name={'terms-and-condition-checkbox'}
            inputProps={{
              'aria-label': 'Terms and conditions checkbox',
              // @ts-expect-error data-testid is a valid prop
              'data-testid': `terms-and-condition-checkbox`,
            }}
          />
          <Box alignItems="center" justifyContent="center" display="flex">
            <Typography>
              I have read and understood the{' '}
              <Link onClick={handleClickTSCS} underline={'always'} style={{ cursor: 'pointer' }} color={'inherit'}>
                terms of use
              </Link>{' '}
              and confirm adherence to them
            </Typography>
          </Box>
        </Grid>
        <Grid sx={{ mt: 2 }}>
          <MessageBox
            message={'An automatic contact note will be created which can be edited for 45 days.'}
            messageType="info"
            hasBorder={true}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};
